import { ReduxUser } from 'app/redux/ReduxInterfaces'
import isHtml from 'is-html'
import { marked } from 'marked'
import { OptionsObject, SnackbarMessage } from 'notistack'
import { ReactElement } from 'react'
import { History } from 'react-router-dom'

interface HandleErrorFeedbackParams {
  enqueueSnackbar: (message: SnackbarMessage, options?: OptionsObject) => void
  history?: History
  message: string | ReactElement
  redirectPath?: string
  timeout?: number
  error?: Error | unknown | null
  logError?: boolean
}

export function valueExistsTS (v): boolean {
  return Boolean(v !== null && v !== undefined && v)
}

export function hasFieldValue (value: any): boolean {
  if (Array.isArray(value)) {
    return value.length > 0
  }

  return value !== false &&
    value !== null &&
    value !== undefined &&
    value !== ''
}

export const handleErrorFeedback = ({
  enqueueSnackbar,
  history = null,
  message,
  redirectPath = '',
  timeout = 2500,
  error = null,
  logError = true
}: HandleErrorFeedbackParams): void => {
  if (logError && (error != null)) {
    console.error('Error:', error)
  }
  enqueueSnackbar(message, { variant: 'error' })
  if (
    history != null &&
    redirectPath != null &&
    redirectPath !== ''
  ) {
    setTimeout(() => {
      history?.push(redirectPath)
    }, Math.max(0, timeout))
  }
}

export const isValidSalesforceId = (
  id: string | null | undefined,
  allowUndefined: boolean = false // sometimes undefined means we're creating a new record, so it's allowed
): boolean => {
  if (id == null) {
    return allowUndefined
  }

  if (typeof id !== 'string') return false

  if (!/^[a-zA-Z0-9]{15}$|^[a-zA-Z0-9]{18}$/.test(id)) {
    return false
  }

  if (id.length === 15) {
    return true
  }

  if (id.length === 18) {
    return isValid18CharSalesforceId(id)
  }

  return false
}

// Helper function to validate 18-character Salesforce ID checksum
const isValid18CharSalesforceId = (id: string): boolean => {
  if (id.length !== 18) return false

  const upperCaseToBit = (char: string): string => char.match(/[A-Z]/) != null ? '1' : '0'
  const binaryToSymbol = (digit: number): string => digit <= 25
    ? String.fromCharCode(digit + 65)
    : String.fromCharCode(digit - 26 + 48)

  const parts = [
    id.slice(0, 5).split('').reverse().map(upperCaseToBit).join(''),
    id.slice(5, 10).split('').reverse().map(upperCaseToBit).join(''),
    id.slice(10, 15).split('').reverse().map(upperCaseToBit).join('')
  ]

  const calculatedChecksum = parts.map(str => binaryToSymbol(parseInt(str, 2))).join('')
  const actualChecksum = id.slice(-3)

  return calculatedChecksum === actualChecksum
}

export function truncate (
  str: string,
  max: number = 20,
  withEllipsis: boolean = true
): string {
  if (str.length <= max) return str
  const truncated = str.slice(0, max)
  return withEllipsis ? `${truncated}…` : truncated
}

/**
 * Checks if a user has all of the specified roles.
 * @param user - The Redux user object.
 * @param role - A single role or array of roles to check.
 * @returns `true` if the user has every specified role, otherwise `false`.
 */
export const hasUserRole = (
  user: ReduxUser,
  role: string | string[]
): boolean => {
  if (user.role == null) return false

  const rolesToCheck = Array.isArray(role) ? role : [role]
  const userRoles = Array.isArray(user.role) ? user.role : [user.role]

  return rolesToCheck.every(r => userRoles.includes(r))
}

/**
 * Checks if a user has at least one of the specified roles.
 * @param user - The Redux user object.
 * @param roles - Array of roles to check against.
 * @returns `true` if the user has any of the specified roles, otherwise `false`.
 */
export const hasUserAnyRole = (
  user: ReduxUser,
  roles: string[]
): boolean => {
  if (user.role == null) return false

  const userRoles = Array.isArray(user.role) ? user.role : [user.role]

  return roles.some(r => userRoles.includes(r))
}

/**
 * Checks if a name is a fake or test name by looking for 'FAKE' or 'TEST' substrings.
 * @param name - The name string to check.
 * @returns `true` if the name contains 'FAKE' or 'TEST' (case-insensitive), otherwise `false`.
 */
export const isFakeName = (name: string): boolean => {
  if (typeof name === 'string' && name !== null) {
    name = name.toUpperCase()
    return name.includes('FAKE') || name.includes('TEST')
  }
  return false
}

/**
 * Checks if a given name or list of names is visible for the user.
 * Names containing 'FAKE' or 'TEST' are only visible to debug users.
 * @param name - A single name or array of names to check.
 * @param user - The Redux user object.
 * @returns `true` if all names are visible to the user, otherwise `false`.
 */
export const isVisibleForUser = (
  name: string | string[],
  user: ReduxUser
): boolean => {
  const arr = Array.isArray(name) ? name : [name]

  return arr.every(name => {
    if (isFakeName(name)) {
      return isDebugOrTesterUser(user)
    }
    return true
  })
}

/**
 * Checks if a user has debug or tester privileges.
 * @param user - The Redux user object.
 * @returns `true` if the user has the 'DEBUG' or 'TESTER' role, otherwise `false`.
 */
export const isDebugUser = (user: ReduxUser): boolean => {
  return hasUserRole(user, 'DEBUG')
}

export const isDebugOrTesterUser = (user: ReduxUser): boolean => {
  return hasUserRole(user, 'DEBUG') || hasUserRole(user, 'TESTER')
}

export function joinWithDifferentLastSeparator (arr: string[], separator: string, lastSeparator: string): string {
  if (!Array.isArray(arr)) return ''
  if (arr.length === 0) return ''
  if (arr.length === 1) return arr[0]
  if (arr.length === 2) return arr[0] + lastSeparator + arr[1]
  return arr.slice(0, -1).join(separator) + lastSeparator + arr[arr.length - 1]
}

/**
 * Checks if a value is falsy without using the `!` operator.
 * Covers: `false`, `0`, `''`, `null`, `undefined`, and `NaN`.
 * @param value - The value to check.
 * @returns `true` if the value is falsy, otherwise `false`.
 */
export function isFalsy (value: unknown): boolean {
  return (
    value === false ||
    value === 0 ||
    value === '' ||
    value === null ||
    value === undefined ||
    Number.isNaN(value)
  )
}

/**
 * Checks whether a string appears to contain Markdown syntax.
 *
 * To save processing time, this function uses a set of regex patterns to detect common Markdown constructs,
 * instead of passing the text through a markdown parser so for uncommon markdown this may return a false negative.
 *
 * @param text - The input text to inspect.
 * @returns `true` when at least one Markdown pattern is detected; otherwise `false`.
 */
export function containsMarkdown (text: string): boolean {
  if (text?.trim() == null) return false

  const patterns = [
    /(^|\n)\s{0,3}#{1,6}\s+\S/, // headings
    /(\*\*|__)[^\n]+(\*\*|__)/, // bold
    /(^|[^\*])\*[^\n]+\*(?!\*)|_[^\n]+_/, // italic
    /`[^`\n]+`|```[\s\S]*?```/, // inline/fenced code
    /\[[^\]]+\]\([^)]+\)/, // links
    /(^|\n)\s*[-*+]\s+\S/, // unordered list
    /(^|\n)\s*\d+\.\s+\S/, // ordered list
    />\s+\S/, // blockquote
    /\|.+\|/ // tables
  ]

  return patterns.some((r) => r.test(text))
}

export const devLog = (...arg): void => {
  if (process.env.NODE_ENV === 'development') {
    console.log(...arg)
  }
}

export function isTruthy<T> (value: T): value is NonNullable<T> {
  return !isFalsy(value)
}

export const isHTMLForMarkdownCheck = (str: string): boolean => {
  if (str == null || str === '' || typeof str !== 'string') return false

  const strWithoutBr = str.replace(/<br\s*\/?>/gi, '').trim()

  return isHtml(strWithoutBr)
}

export const convertMarkdownToHtmlIfNeeded = (text: string): string => {
  if (text == null) return ''

  if (isHTMLForMarkdownCheck(text)) {
    return text
  } else {
    const rawHtml = marked.parse(text) as string
    return rawHtml
  }
}
